import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { createSelector } from "reselect";
import TableContainer from "../../Components/Common/TableContainer";
import DeleteModal from "../../Components/Common/DeleteModal";
import { useProfile } from "../../Components/Hooks/UserHooks";
import { toast, ToastContainer } from "react-toastify";
import {
  getClients as onGetClients,
  deleteClient as onDeleteClient,
} from "../../slices/thunks";

const selectClientProperties = createSelector(
    (state) => state.Clients,
    (client) => ({
      clients: client.clients || [],
      isClientSuccess: client.isClientSuccess,
      error: client.error,
    })
);

const createCommonColumn = (header, accessorKey) => ({
  header,
  accessorKey,
  enableColumnFilter: false,
  headerStyle: { textAlign: "center" },
  cell: (cell) => <div className="text-center">{cell.getValue()}</div>,
});

const createStatusColumn = () => ({
  header: "Status",
  accessorKey: "id",
  enableColumnFilter: false,
  headerStyle: { textAlign: "center" },
  cell: (cell) => {
    const row = cell.row.original;
    const hasNoContracts = row.contracts_count === null || row.contracts_count === 0 ? true : false ;
    const isAdditionalContact = row.additional_contact1? true : false;

    return (
        <div className="d-flex align-items-center justify-content-center gap-2">
          {hasNoContracts && (
              <span className="badge bg-warning d-flex align-items-center p-2 rounded">
            <i className="ri-file-warning-line me-1 fs-5"></i>
            No Contracts
          </span>
          )}
          {isAdditionalContact && (
              <span className="badge bg-info d-flex align-items-center p-2 rounded">
            <i className="ri-contacts-line me-1 fs-5"></i>
            Additional Contact
          </span>
          )}
          {!hasNoContracts && !isAdditionalContact && (
              <span className="badge bg-success d-flex align-items-center p-2 rounded">
            <i className="ri-checkbox-circle-line me-1 fs-5"></i>
            Active
            <span className="badge bg-light text-dark ms-2 px-2 py-1 rounded-pill">
              {row.contracts_count}
            </span>
          </span>
          )}
        </div>
    );
  },
});

const createActionColumn = (deleteRight, onClickDelete) => ({
  id: "actions",
  enableColumnFilter: false,
  header: "",
  size: 60,
  headerStyle: { textAlign: "center" },
  cell: (cellProps) => {

    return (
        <div className="text-center">
          <ul className="list-inline d-flex justify-content-end align-items-center gap-2 mb-0">
            {deleteRight && (
                <li className="list-inline-item">
                  <button
                      to="#"
                      className="btn btn-sm btn-icon btn-ghost-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickDelete(cellProps.row.original);
                      }}
                  >
                    <i className="ri-delete-bin-5-fill fs-16"></i>
                  </button>
                </li>
            )}
          </ul>
        </div>
    );
  },
});

const getNaturalPersonColumns = (deleteRight, onClickDelete) => [
  createCommonColumn("Name", "name"),
  createCommonColumn("Surname", "surname"),
  createCommonColumn("Address", "address"),
  createCommonColumn("Mobile Phone", "mobile_phone_number"),
  createCommonColumn("Personal Email", "personal_email"),
  createStatusColumn(),
  createActionColumn(deleteRight, onClickDelete),
];

const getLegalPersonColumns = (deleteRight, onClickDelete) => [
  createCommonColumn("Company Name", "legal_company_name"),
  createCommonColumn("Fiscal Code", "legal_fiscal_code"),
  createCommonColumn("Business Email", "business_email"),
  createCommonColumn("Registration Code", "legal_unique_registration_code"),
  createCommonColumn("Trade Register Number", "legal_trade_register_number"),
  createCommonColumn("VAT Payer", "legal_vat_payer"),
  createStatusColumn(),
  createActionColumn(deleteRight, onClickDelete),
];

const ClientTable = ({ clientType, filteredData }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isClientSuccess, error } = useSelector(selectClientProperties);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const { userProfile, isAdmin, isEditor } = useProfile();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [processedData, setProcessedData] = useState([]);

  // Process data to identify additional contacts
  useEffect(() => {
    if (filteredData) {
      // Create a Set of all IDs that are additional contacts
      const additionalContactIds = new Set();

      filteredData.forEach(client => {
        if (client.additional_contact1) additionalContactIds.add(client.additional_contact1);
        if (client.additional_contact2) additionalContactIds.add(client.additional_contact2);
      });

      // Add a flag to each client that is an additional contact
      const processed = filteredData.map(client => ({
        ...client,
        isAdditionalContact: additionalContactIds.has(client.id)
      }));

      setProcessedData(processed);
    }
  }, [filteredData]);

  const onClickDelete = useCallback((client) => {
    setSelectedClient(client);
    setDeleteModal(true);
  }, []);

  const handleDeleteClient = useCallback(async () => {
    if (selectedClient) {
      setIsDeleting(true);
      setDeleteError(null);
      try {
        await dispatch(onDeleteClient(selectedClient.id)).unwrap();
        setDeleteModal(false);
        setSelectedClient(null);
        // Refresh the clients list
        const queryParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );
        await dispatch(onGetClients(queryParams));
      } catch (err) {
        const errorMessage =
            err.response?.data?.message ||
            err.message ||
            "Failed to delete client";
        setDeleteError(errorMessage);
      } finally {
        setIsDeleting(false);
      }
    }
  }, [dispatch, selectedClient, location.search]);

  const columns = useMemo(() => {
    return clientType === "NATURAL"
        ? getNaturalPersonColumns(isAdmin || isEditor, onClickDelete)
        : getLegalPersonColumns(isAdmin || isEditor, onClickDelete);
  }, [clientType, userProfile, onClickDelete]);

  const handleRowClick = useCallback((row) => {
    navigate(`/client-details/${row.original.id}`);
  }, [navigate]);

  return (
      <div>
        {isClientSuccess ? (
            processedData && processedData.length > 0 ? (
                <TableContainer
                    columns={columns}
                    data={processedData}
                    customPageSize={10}
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light text-muted text-uppercase text-center"
                    handleRowClick={handleRowClick}
                    customTableRowProps={(row) => ({
                      className: "cursor-pointer",
                      style: { cursor: "pointer" }
                    })}
                />
            ) : (
                <div className="text-center mt-5">
                  <p className="text-muted">No clients available.</p>
                </div>
            )
        ) : (
            <div className="text-center mt-5">
              <p className="text-muted">
                {error || "Error loading clients. Please try again."}
              </p>
            </div>
        )}

        <DeleteModal
            show={deleteModal}
            onDeleteClick={handleDeleteClient}
            onCloseClick={() => {
              setDeleteModal(false);
              setSelectedClient(null);
              setDeleteError(null);
            }}
            isDeleting={isDeleting}
            error={deleteError}
        />

        <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
        />
      </div>
  );
};

export default ClientTable;
